import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { InlineAlert } from '@rbilabs/components-library';
import { Icon } from '@rbilabs/components-library/build/components/icon';
import { Tab, TabList } from '@rbilabs/components-library/build/components/tabs';
import { useIntl } from 'react-intl';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { useLocation, useMatch } from 'react-router';

import { Tile } from 'components/features/components/marketing-tile-group/marketing-tile-group';
import { HorizontalPosition, TileSize } from 'components/marketing-tile';
import { ModalQuestUnlockedIncentive } from 'components/modal-quest-unlocked-incentive';
import { LoyaltyTierKey } from 'generated/graphql-gateway';
import { useFavoriteOffers } from 'hooks/favorite-offers';
import { useOffersPageMarketingTiles } from 'hooks/use-combine-offers-and-marketing-tiles';
import { useEnableGreyedOutOffers } from 'hooks/use-enable-greyed-out-offers';
import { useFeatureInvitationCode } from 'hooks/use-feature-invitation-code';
import { useLoyaltyExternalOffers } from 'hooks/use-loyalty-external-offers';
import { useLoyaltyExternalOffersEmptyStates } from 'hooks/use-loyalty-external-offers-empty-states';
import { useLoyaltyOffersMarketingTiles } from 'hooks/use-loyalty-offers-marketing-tiles';
import { MediaQuery, useMediaQuery } from 'hooks/use-media-query';
import { IncentiveCardSkeleton } from 'pages/loyalty/loyalty-incentives-components/incentive-card/incentive-card-skeleton';
import { OffersEmptyState } from 'pages/loyalty/loyalty-offers/offers-empty-state';
import { useAuthContext } from 'state/auth';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames } from 'state/cdp/constants';
import { useGeolocation } from 'state/geolocation';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyQuests } from 'state/loyalty/hooks/use-loyalty-quests';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useLoyaltyUserTier } from 'state/loyalty/hooks/use-loyalty-user-tier';
import {
  ICartEntryType,
  useInRestaurantRedemptionContext,
} from 'state/loyalty/in-restaurant-redemption';
import { ExternalOffer, LoyaltyOffer } from 'state/loyalty/types';
import { isDiscountLoyaltyOffer, isTestOnly, isUpcomingOffer } from 'state/loyalty/utils';
import { useNearbyStoresContext } from 'state/nearby-stores';
import { useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { LaunchDarklyFlag } from 'utils/launchdarkly';
import { toast } from 'utils/toast';

import { IncentiveItemsContainer, LoyaltyIncentivesGrid } from '../loyalty-incentives-components';
import { ExternalIncentiveCard } from '../loyalty-incentives-components/external-incentive-card/external-incentive-card';
import { ExternalIncentiveCardDescription } from '../loyalty-incentives-components/external-incentive-card/external-incentive-card.styled';
import { IncentiveCard } from '../loyalty-incentives-components/incentive-card/incentive-card';
import { IncentiveCardDescription } from '../loyalty-incentives-components/incentive-card/incentive-card.styled';
import { parseIncentiveData } from '../loyalty-incentives-components/incentive-card/parse-incentive-data';

import { LoyaltyEndOfOffers } from './end-of-offers/loyalty-end-of-offers';
import { ExternalOffersEmptyState } from './external-offers-empty-state';
import { FavoritesEmptyState } from './favorite-empty-state';
import {
  LoyaltyIncentivesContainer,
  OffersSkeletonPromoCode,
  OffersSkeletonTabs,
  TabContainer,
  TabDisclaimer,
} from './loyalty-offers.styled';
import { LoyaltyPromoCode } from './loyalty-promo-code';
import { OffersInCart } from './offers-in-cart/offers-in-cart';
import { AvailableTabs, AvailableTabsType } from './types';
import { useOfferView } from './useOfferView';
import { isExternalOfferClosed, isGreyedOutOffer } from './utils';

type TLocationState = { selectedTab?: AvailableTabsType; backRoute?: string } | undefined;

export const OfferViewV1 = () => {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const state = location.state as TLocationState;
  const { user } = useAuthContext();
  const { loyaltyUser } = useLoyaltyUser();
  const { refetchUserOffers } = useLoyaltyContext();
  const { isFavorite, toggleFavorite, isOfferLoading } = useFavoriteOffers();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const offersLoading = useAppSelector(selectors.loyalty.selectOffersLoading);

  const loyaltyOffersInCooldown = useAppSelector(selectors.loyalty.selectLoyaltyOffersInCooldown);
  const dispatch = useAppDispatch();
  const { inRestaurantRedemptionCart, removeInRestaurantRedemptionEntry } =
    useInRestaurantRedemptionContext();

  const enableFavoriteOffers = useFlag(LaunchDarklyFlag.ENABLE_FAVORITE_OFFERS);
  const enableLoyaltyPromoCodes = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_PROMO_CODES);
  const enableGeolocation = useFlag(LaunchDarklyFlag.ENABLE_SETTINGS_GEOLOCATION);
  const { removeFromCart } = useOrderContext();
  const { address1: addressOfNearestStore, restaurantGroup } = useNearbyStoresContext();
  const { noStoreSelected, restaurantLogAttributesRef } = useStoreContext();
  const { isAuthenticated } = useAuthContext();
  const { enableInvitationCode } = useFeatureInvitationCode();
  const { marketingTiles } = useLoyaltyOffersMarketingTiles();
  const { externalOffersEmptyStates: enableExternalOffersEmptyState } =
    useLoyaltyExternalOffersEmptyStates();
  const { externalOffers: originalExternalOffers } = useLoyaltyExternalOffers();
  // Enable External Offers only when the Live External Offers document is published in Sanity
  const enableExternalOffers =
    originalExternalOffers || enableExternalOffersEmptyState ? true : false;
  const userIsAuthenticated = isAuthenticated();

  const { isPermissionGranted } = useGeolocation();
  const { enableGreyedOutOffers } = useEnableGreyedOutOffers(userIsAuthenticated);
  const isMobile = useMediaQuery(MediaQuery.Mobile);

  const { params } =
    useMatch({
      path: `${formatMessage({ id: 'routes.loyaltyHome' })}/:tab/*`,
    }) || {};

  const loyaltyEngineId = params?.['*'];
  const tabPath = params?.tab! || '';

  const cdp = useCdpContext();
  const { loyaltyTierKey: userTier } = useLoyaltyUserTier();

  const offerContainerRef = React.useRef<HTMLDivElement | null>(null);

  // External Offers can be accessible for not authenticated Users
  const dynamicAvailableTabs = useMemo(() => {
    if (enableExternalOffers) {
      return userIsAuthenticated
        ? { AllOffers: 0, ExternalOffers: 1, Favorites: 2 }
        : { AllOffers: 0, ExternalOffers: 1 };
    }
    return { AllOffers: 0, Favorites: 1 };
  }, [enableExternalOffers, userIsAuthenticated]);

  const [selectedTab, setSelectedTab] = useState<AvailableTabs>(
    dynamicAvailableTabs[state?.selectedTab ?? 'AllOffers'] as AvailableTabs
  );

  const {
    challengeIncentives,
    onChallengeIcentiveSelect,
    challengeIncentiveSelected,
    questsLoading,
    onChangeUnlockedIncentivesModalVisibilityStatus,
    unlockedIncentivesModalVisibilityStatus,
  } = useLoyaltyQuests(loyaltyUser?.id ?? '');

  const offerUpdatedMessage = useMemo(() => {
    return restaurantGroup?.name
      ? formatMessage(
          { id: 'offersUpdatedByLocation' },
          {
            location: restaurantGroup?.name,
          }
        )
      : formatMessage({ id: 'offersUpdated' });
  }, [formatMessage, restaurantGroup]);

  const shouldRenderOfferUpdatedMessage = useMemo(
    () => enableGeolocation && noStoreSelected && (restaurantGroup?.name || addressOfNearestStore),
    [addressOfNearestStore, enableGeolocation, noStoreSelected, restaurantGroup]
  );

  // TODO: extract this method to a reusable place
  const removeLoyaltyOffer = (offerToRemove: LoyaltyOffer) => {
    const appliedOfferToRemove = appliedOffers.find(
      offer => offer.id === offerToRemove.loyaltyEngineId
    );
    if (appliedOfferToRemove?.cartId) {
      removeFromCart(appliedOfferToRemove);
      return true;
    }

    const redemptionOfferToRemove = inRestaurantRedemptionCart.find(
      cartEntry =>
        cartEntry.type === ICartEntryType.OFFER && cartEntry.details.offer._id === offerToRemove._id
    );
    if (redemptionOfferToRemove) {
      removeInRestaurantRedemptionEntry(redemptionOfferToRemove);
      return true;
    }

    return false;
  };

  const handleRemove = (offerToRemove: LoyaltyOffer) => {
    let showNotification = removeLoyaltyOffer(offerToRemove);

    if (isDiscountLoyaltyOffer(offerToRemove)) {
      dispatch(actions.loyalty.setSelectedOffer(null));
      dispatch(actions.loyalty.resetAppliedOffers());
      showNotification = true;
    }

    if (showNotification) {
      toast.success(formatMessage({ id: 'offerAppliedSuccess' }));
    }
  };

  const externalOffers = originalExternalOffers as ExternalOffer[];

  const {
    fetchOffers,
    onPromoCodeRedeemSuccess,
    offers,
    offersInCart,
    loyaltyId,
    selectedIncentive,
    handleClick,
  } = useOfferView({
    enableLoyaltyPromoCodes,
    loyaltyUser,
    user,
    appliedOffers,
    inRestaurantRedemptionCart,
    loyaltyEngineId,
    enableGeolocation,
    offersLoading,
    tabPath,
    externalOffers,
    challengeIncentives,
    onChallengeIcentiveSelect,
    isMobile,
  });

  // Refetch offers if route changes to base route
  useEffect(() => {
    const isBaseOffersRoute = !loyaltyEngineId;
    if (isBaseOffersRoute) {
      fetchOffers();
    }
  }, [fetchOffers, loyaltyEngineId]);

  const { sortedTilesAndOffers, marketingTileTop, marketingTileBottom } =
    useOffersPageMarketingTiles({
      offers,
      offersPageMarketingTiles: marketingTiles,
      selectedOffer: selectedIncentive || null,
    });

  const handleTabChange = useCallback(
    (event: React.ChangeEvent, value: number) => {
      event.preventDefault();
      const tabName = Object.keys(dynamicAvailableTabs)[value];
      cdp.logNavigationClick(CustomEventNames.BUTTON_CLICK, {
        name: tabName,
        ...restaurantLogAttributesRef.current,
      });
      setSelectedTab(value);
    },
    [cdp, dynamicAvailableTabs]
  );

  const handleRefetch = async () => {
    if (loyaltyId) {
      refetchUserOffers({
        loyaltyId,
      });
    }
    fetchOffers();
  };

  const shouldHideSelectedOffer =
    selectedTab === dynamicAvailableTabs.Favorites &&
    offersInCart.some(offer => !isFavorite(offer?.loyaltyEngineId || ''));

  const loadingNearbyStore = useMemo(
    () =>
      enableGeolocation
        ? isPermissionGranted && !addressOfNearestStore && addressOfNearestStore !== null
        : false,
    [addressOfNearestStore, enableGeolocation, isPermissionGranted]
  );

  // Array containing the engineId of offers used as upsell options
  const upsellOffers = offers?.reduce((acc: string[], offer) => {
    if (offer.upsellOptions?.length) {
      offer.upsellOptions.forEach(upsellOffer => acc.push(upsellOffer?.loyaltyEngineId!));
    }
    return acc;
  }, []);

  const offersAndTiles = sortedTilesAndOffers
    ?.map((offerOrTile: any, idx: number) => {
      const isOffer =
        offerOrTile?._type === 'systemwideOffer' || offerOrTile?._type === 'configOffer';

      // If we selected the Favorites tab we filter any non-favorited offer / tile
      // If we selected the External Offers tab we don't want to display any Loyalty Offer
      if (
        (enableFavoriteOffers &&
          selectedTab === dynamicAvailableTabs.Favorites &&
          !isFavorite(offerOrTile._id || '')) ||
        (enableExternalOffers && selectedTab === dynamicAvailableTabs.ExternalOffers) ||
        isTestOnly(offerOrTile)
      ) {
        return null;
      }

      const offerIsUpcoming = isUpcomingOffer(offerOrTile);

      if (isOffer) {
        if (upsellOffers.includes(offerOrTile?.loyaltyEngineId) || isTestOnly(offerOrTile)) {
          return null;
        }

        const incentiveData = parseIncentiveData(offerOrTile);

        return (
          <IncentiveCard
            key={offerOrTile.loyaltyEngineId || idx}
            incentiveData={incentiveData}
            handleClick={handleClick}
            locked={enableGreyedOutOffers && isGreyedOutOffer(offerOrTile, userIsAuthenticated)}
            favoriteEnabled={enableFavoriteOffers && userIsAuthenticated}
            favorited={enableFavoriteOffers && isFavorite(offerOrTile._id || '')}
            favoriteLoading={isOfferLoading(offerOrTile._id || '')}
            handleToggleFavorite={toggleFavorite}
            isUpcomingOffer={offerIsUpcoming}
          >
            {incentiveData.description && (
              <IncentiveCardDescription content={incentiveData.description} />
            )}
          </IncentiveCard>
        );
      }
      return (
        <Tile
          tile={offerOrTile}
          key={offerOrTile?._id}
          size={TileSize.HALF}
          buttonPosition={HorizontalPosition.RIGHT}
          hasBorder={false}
          additionalPadding={`0.5rem`}
        />
      );
    })
    .filter((item: any) => !!item);

  const availableExternalOffers = externalOffers?.map((offer: ExternalOffer, idx: number) => {
    const isOfferClosed = isExternalOfferClosed(offer, userTier, isAuthenticated());

    if (
      (enableFavoriteOffers &&
        selectedTab === dynamicAvailableTabs.Favorites &&
        !isFavorite(offer._id || '')) ||
      isTestOnly(offer)
    ) {
      return null;
    }

    const offerTier = offer.tierIncentiveList?.[0]?.tierName as LoyaltyTierKey;

    return (
      <ExternalIncentiveCard
        key={offer._id || idx}
        incentiveData={offer}
        handleClick={handleClick}
        favoriteEnabled={enableFavoriteOffers && isAuthenticated()}
        favorited={enableFavoriteOffers && isFavorite(offer._id || '')}
        favoriteLoading={isOfferLoading(offer._id || '')}
        handleToggleFavorite={toggleFavorite}
        isOfferClosed={isOfferClosed}
        offerTier={offerTier}
      >
        {offer.subtitle && (
          <ExternalIncentiveCardDescription>
            {offer.subtitle.locale}
          </ExternalIncentiveCardDescription>
        )}
      </ExternalIncentiveCard>
    );
  });

  const isExternalOffer = (offer: LoyaltyOffer | ExternalOffer | undefined) =>
    offer?.__typename === 'ExternalOffers';

  const favoritesEmptyState =
    !sortedTilesAndOffers?.some(offer => isFavorite(offer._id)) &&
    !externalOffers?.some((offer: ExternalOffer) => isFavorite(offer._id));

  const externalOffersEmptyState = !externalOffers || !availableExternalOffers?.length;

  const tabsArray = [
    <Tab
      key="offersAll"
      aria-controls="tabpanel-all"
      id="tab-all"
      label={formatMessage({ id: 'offersAll' })}
      selected={selectedTab === dynamicAvailableTabs.AllOffers}
    />,
    enableExternalOffers ? (
      <Tab
        key="offersExternal"
        aria-controls="tabpanel-externals"
        id="tab-externals"
        label={formatMessage({ id: 'offersExternal' })}
        selected={selectedTab === dynamicAvailableTabs.ExternalOffers}
      />
    ) : null,
    isAuthenticated() ? (
      <Tab
        key="offersFavorites"
        aria-controls="tabpanel-favorites"
        id="tab-favorites"
        label={formatMessage({ id: 'offersFavorites' })}
        selected={selectedTab === dynamicAvailableTabs.Favorites}
      />
    ) : null,
  ].filter(el => !!el);

  const shouldShowLoaderTiles = offersLoading && !offersAndTiles?.length;
  const shouldShowLoaderTabs = offersLoading && !tabsArray.length;

  return (
    <LoyaltyIncentivesContainer ref={offerContainerRef}>
      {shouldRenderOfferUpdatedMessage && !loadingNearbyStore && (
        <InlineAlert message={offerUpdatedMessage} icon="checkCircle" />
      )}
      <SkeletonTheme baseColor="#0000000a" highlightColor="#0000001a">
        {((enableFavoriteOffers && isAuthenticated()) ||
          (enableExternalOffers && !isAuthenticated())) &&
          !(selectedIncentive && isMobile) && (
            <TabContainer>
              {shouldShowLoaderTabs ? (
                Boolean(user) && (
                  <OffersSkeletonTabs>
                    <Skeleton width={'3rem'} height={'1.5rem'} />
                    <Skeleton width={'3rem'} height={'1.5rem'} />
                    <Skeleton width={'3rem'} height={'1.5rem'} />
                  </OffersSkeletonTabs>
                )
              ) : (
                <TabList selectedIndex={selectedTab} onChange={handleTabChange}>
                  {tabsArray as JSX.Element[]}
                </TabList>
              )}
            </TabContainer>
          )}

        {enableFavoriteOffers &&
          selectedTab === dynamicAvailableTabs.Favorites &&
          !selectedIncentive && (
            <TabDisclaimer>
              <Icon icon={'info'} color={'tertiary'} width="22px" aria-hidden />
              {formatMessage({ id: 'offersFavoritesDisclaimer' })}
            </TabDisclaimer>
          )}

        {enableLoyaltyPromoCodes &&
        selectedTab !== dynamicAvailableTabs.ExternalOffers &&
        !isExternalOffer(selectedIncentive) ? (
          !(selectedIncentive && isMobile) && shouldShowLoaderTiles ? (
            <OffersSkeletonPromoCode>
              <Skeleton height={'2.5rem'} width={'2.5rem'} circle />
              <Skeleton height={'1.25rem'} width={'180px'} />
            </OffersSkeletonPromoCode>
          ) : (
            <LoyaltyPromoCode onPromoCodeRedeemSuccess={onPromoCodeRedeemSuccess} />
          )
        ) : null}

        <LoyaltyIncentivesGrid
          selectedIncentive={selectedIncentive}
          refetch={handleRefetch}
          goBack={state?.backRoute || `${formatMessage({ id: 'routes.loyaltyHome' })}/${tabPath}`}
          engineIncentivesMap={{}}
          offersInCooldown={loyaltyOffersInCooldown}
          challengeIncentiveSelected={challengeIncentiveSelected}
          offersLoadingState={offersLoading}
        >
          <IncentiveItemsContainer>
            {Boolean(offersInCart?.length) && !shouldHideSelectedOffer && (
              <OffersInCart
                offers={offersInCart}
                handleClick={handleClick}
                handleRemove={handleRemove}
                favoriteEnabled={enableFavoriteOffers && isAuthenticated()}
              />
            )}
            {!questsLoading &&
              challengeIncentives?.map(challengeIncentive => {
                const parsedChallengeIncentive = parseIncentiveData(challengeIncentive);

                // Do not show the challenge Incentive on Favorites tab
                // except when it is favorite
                return (
                  (selectedTab !== dynamicAvailableTabs.Favorites ||
                    (selectedTab === dynamicAvailableTabs.Favorites &&
                      isFavorite(challengeIncentive._id || ''))) && (
                    <IncentiveCard
                      key={challengeIncentive.loyaltyEngineId}
                      isQuestChallengeIcentive
                      incentiveData={parsedChallengeIncentive}
                      handleClick={handleClick}
                      favoriteEnabled={enableFavoriteOffers && isAuthenticated()}
                      favorited={enableFavoriteOffers && isFavorite(challengeIncentive._id || '')}
                      favoriteLoading={isOfferLoading(challengeIncentive._id ?? '')}
                      handleToggleFavorite={toggleFavorite}
                    ></IncentiveCard>
                  )
                );
              })}
            {marketingTileTop &&
              selectedTab !== dynamicAvailableTabs.Favorites &&
              selectedTab !== dynamicAvailableTabs.ExternalOffers &&
              !offersLoading && (
                <Tile
                  data-testid="top-marketing-tile"
                  tile={marketingTileTop}
                  size={TileSize.HALF}
                  buttonPosition={HorizontalPosition.RIGHT}
                  hasBorder={false}
                  additionalPadding={`0.5rem`}
                />
              )}

            {shouldShowLoaderTiles ? (
              <>
                {new Array(6).fill('').map((_, index) => (
                  <IncentiveCardSkeleton key={index} />
                ))}
              </>
            ) : (
              offersAndTiles
            )}

            {((externalOffers && selectedTab === dynamicAvailableTabs.ExternalOffers) ||
              (enableFavoriteOffers && selectedTab === dynamicAvailableTabs.Favorites)) &&
              !offersLoading &&
              availableExternalOffers}

            {marketingTileBottom &&
              selectedTab !== dynamicAvailableTabs.Favorites &&
              selectedTab !== dynamicAvailableTabs.ExternalOffers &&
              !shouldShowLoaderTiles && (
                <Tile
                  data-testid="bottom-marketing-tile"
                  tile={marketingTileBottom}
                  size={TileSize.HALF}
                  buttonPosition={HorizontalPosition.RIGHT}
                  hasBorder={false}
                  additionalPadding={`0.5rem`}
                />
              )}
            {!offersAndTiles?.length &&
              !shouldShowLoaderTiles &&
              selectedTab === dynamicAvailableTabs.AllOffers && <OffersEmptyState />}
            {enableFavoriteOffers &&
              selectedTab === dynamicAvailableTabs.Favorites &&
              favoritesEmptyState && <FavoritesEmptyState />}
            {enableExternalOffers &&
              selectedTab === dynamicAvailableTabs.ExternalOffers &&
              externalOffersEmptyState && <ExternalOffersEmptyState />}
            {Boolean(offers?.length) && <LoyaltyEndOfOffers />}
          </IncentiveItemsContainer>
        </LoyaltyIncentivesGrid>

        {enableInvitationCode && loyaltyUser && (
          <ModalQuestUnlockedIncentive
            onChangeUnlockedIncentivesModalVisibilityStatus={
              onChangeUnlockedIncentivesModalVisibilityStatus
            }
            unlockedIncentivesModalVisibilityStatus={unlockedIncentivesModalVisibilityStatus}
          />
        )}
      </SkeletonTheme>
    </LoyaltyIncentivesContainer>
  );
};
